import React, { useCallback, useContext } from 'react';
import { connect } from 'react-redux';
import { useUIDSeed } from 'react-uid';
import { ThemeContext } from 'styled-components';

import AlertsIcon from 'mdi-react/BellOutlineIcon';
import AlertsIconActive from 'mdi-react/BellIcon';
import ListsIcon from 'mdi-react/HeartOutlineIcon';
import ListsIconActive from 'mdi-react/HeartIcon';
import AnalyticsIcon from 'mdi-react/PollBoxIcon';
import FilterIcon from 'mdi-react/FilterVariantIcon';

import { useAppContext } from '@prisjakt/ui-app-context';
import ExperimentEditor from '@prisjakt/ui-experiment/src/editor';
import { layout } from '@prisjakt/ui-base';
import UserAvatar from '@prisjakt/ui-user-avatar';
import Menu from '@prisjakt/ui-menu';
import MenuItem from '@prisjakt/ui-menu/src/components/menu-item';

import { useViewportContext } from '../../../../../../components/viewport/context';
import AccountMenu from '../../../../../account/menu-new';

import {
  openLists as openListsAction,
  closeLists as closeListsAction,
} from '../../../../actions/lists';
import {
  openAlerts as openAlertsAction,
  closeAlerts as closeAlertsAction,
} from '../../../../actions/alert';
import filterActions from '../../../../actions/filter';

import { trackOpeningSidebar, trackClosingSidebar } from '../../../../tracking';

import { SIDEBAR_STATE, DEFAULT_SIDEBAR_STATE } from '../../../../constants';

import { AppState } from '../../../../../../reducers';
import { User } from '../../../../../../types';

import * as S from './styles';

type OwnProps = {};

type StateProps = {
  me: User;
  tab:
    | SIDEBAR_STATE.NONE
    | SIDEBAR_STATE.LISTS
    | SIDEBAR_STATE.ALERTS
    | SIDEBAR_STATE.FILTER;
  previousTab:
    | SIDEBAR_STATE.LISTS
    | SIDEBAR_STATE.ALERTS
    | SIDEBAR_STATE.FILTER;
  showFilter: boolean;
  showHorizontalFilter: boolean;
};

type DispatchProps = {
  closeLists: () => void;
  openLists: () => void;
  openAlerts: () => void;
  toggleFilter: () => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const SidebarMenuBar = ({
  tab = SIDEBAR_STATE.NONE,
  previousTab = SIDEBAR_STATE.LISTS,
  closeLists = () => {},
  openLists = () => {},
  openAlerts = () => {},
  toggleFilter = () => {},
  me,
  showFilter,
  showHorizontalFilter,
}: Props) => {
  const { isDesktop } = useViewportContext();
  const { t } = useAppContext();
  const theme = useContext(ThemeContext);

  // Generate unique UIDs to use as HTML ids
  const getId = useUIDSeed();
  const listsTabId = getId('sidebar-desktop-lists');
  const alertsTabId = getId('sidebar-desktop-alerts');
  const filterTabId = getId('sidebar-desktop-filter');

  const isSidebarOpen = tab !== SIDEBAR_STATE.NONE;
  const isListTabActive = tab === SIDEBAR_STATE.LISTS;
  const isAlertsTabActive = tab === SIDEBAR_STATE.ALERTS;
  const isFilterTabActive = tab === SIDEBAR_STATE.FILTER;

  const closeSidebar = useCallback(() => {
    closeLists();
  }, [closeLists]);

  const toggleView = useCallback(
    state => {
      switch (state) {
        case tab:
          closeSidebar();
          break;
        case SIDEBAR_STATE.LISTS:
          openLists();
          break;
        case SIDEBAR_STATE.ALERTS:
          openAlerts();
          break;
        case SIDEBAR_STATE.FILTER:
          toggleFilter();
          break;
        default:
          closeSidebar();
      }
    },
    [closeSidebar, openAlerts, openLists, tab, toggleFilter]
  );

  const handleListsMenuButton = useCallback(() => {
    if (tab === SIDEBAR_STATE.LISTS) {
      trackClosingSidebar(SIDEBAR_STATE.LISTS, 'sidebarListsButton');
    } else {
      trackOpeningSidebar(SIDEBAR_STATE.LISTS, 'sidebarListsButton');
    }

    toggleView(SIDEBAR_STATE.LISTS);
  }, [toggleView, tab]);

  const handleAlertsMenuButton = useCallback(() => {
    if (tab === SIDEBAR_STATE.ALERTS) {
      trackClosingSidebar(SIDEBAR_STATE.ALERTS, 'sidebarAlertsButton');
    } else {
      trackOpeningSidebar(SIDEBAR_STATE.ALERTS, 'sidebarAlertsButton');
    }

    toggleView(SIDEBAR_STATE.ALERTS);
  }, [toggleView, tab]);

  const handleToggleButton = useCallback(() => {
    if (isSidebarOpen) {
      trackClosingSidebar(tab, 'sidebarToggleButton');
      toggleView(SIDEBAR_STATE.NONE);
      return;
    }

    // Open default view if previous view was filter but no longer
    // on a leaf category page, or if filter is set to HorizontalFilter,
    // or `previousTab` is empty.
    if (
      !previousTab ||
      (previousTab === SIDEBAR_STATE.FILTER && !showFilter) ||
      (previousTab === SIDEBAR_STATE.FILTER && showHorizontalFilter)
    ) {
      trackOpeningSidebar(DEFAULT_SIDEBAR_STATE, 'sidebarToggleButton');
      toggleView(DEFAULT_SIDEBAR_STATE);
      return;
    }

    trackOpeningSidebar(previousTab, 'sidebarToggleButton');
    toggleView(previousTab);
  }, [
    isSidebarOpen,
    previousTab,
    showFilter,
    showHorizontalFilter,
    toggleView,
    tab,
  ]);

  return (
    <S.SidebarMenu>
      <S.TopBar>
        <S.MenuButton
          active={false}
          hoverEffect="background"
          onClick={handleToggleButton}
          aria-label={
            isSidebarOpen
              ? t('symbols', 'symbol.collapse-sidebar')
              : t('symbols', 'symbol.expand-sidebar')
          }
          aria-expanded={isSidebarOpen}
          aria-haspopup="true"
        >
          <S.ToggleSidebarIcon size={24} open={isSidebarOpen} />
        </S.MenuButton>

        <S.MenuButton
          active={isListTabActive}
          onClick={handleListsMenuButton}
          data-test="MenuButtonLists"
          aria-label={t('lists', 'lists.title')}
          aria-expanded={isListTabActive}
          aria-haspopup="true"
          aria-controls={listsTabId}
        >
          {isListTabActive ? <ListsIconActive /> : <ListsIcon />}
        </S.MenuButton>
        <S.MenuButton
          active={isAlertsTabActive}
          onClick={handleAlertsMenuButton}
          data-test="MenuButtonAlerts"
          aria-label={t('alerts', 'alerts.title')}
          aria-expanded={isAlertsTabActive}
          aria-haspopup="true"
          aria-controls={alertsTabId}
        >
          {isAlertsTabActive ? <AlertsIconActive /> : <AlertsIcon />}
        </S.MenuButton>

        <S.MenuButton
          active={isFilterTabActive}
          onClick={() => toggleView(SIDEBAR_STATE.FILTER)}
          hidden={!showFilter || showHorizontalFilter}
          data-test="MenuButtonFilter"
          aria-label={t('filter', 'filter.title')}
          aria-expanded={isFilterTabActive}
          aria-haspopup="true"
          aria-controls={filterTabId}
        >
          <FilterIcon />
        </S.MenuButton>
      </S.TopBar>

      {isDesktop && (
        <S.BottomBar>
          {me?.analyticsAccess && (
            <Menu
              toggleElementStyles={{
                width: layout.sidebarMenuWidth.desktop.rem,
              }}
              toggleElement={
                <S.MenuButton
                  active={false}
                  hoverEffect="color"
                  aria-label={t('account', 'menu.pricespy-analytics')}
                >
                  <AnalyticsIcon />
                </S.MenuButton>
              }
              position="right"
              grow="up"
              width="200px"
            >
              {me.analyticsAccess.stores?.map(
                store =>
                  store && (
                    <MenuItem
                      key={store.name}
                      href={store.url}
                      data-test="MenuItemStore"
                    >
                      {store.name}
                    </MenuItem>
                  )
              )}
              {me.analyticsAccess.brands?.map(
                brand =>
                  brand && (
                    <MenuItem
                      key={brand.name}
                      href={brand.url}
                      data-test="MenuItemBrand"
                    >
                      {brand.name}
                    </MenuItem>
                  )
              )}
            </Menu>
          )}

          <AccountMenu
            position="right"
            grow="up"
            toggleElement={
              <S.MenuButton
                active={false}
                hoverEffect="color"
                aria-label={t('account', 'menu.menu-button')}
              >
                <UserAvatar {...me} size={24} />
              </S.MenuButton>
            }
            toggleElementStyles={{
              width: layout.sidebarMenuWidth.desktop.rem,
            }}
            toggleElementActiveStyles={{
              color: theme.icons.callToAction,
            }}
          />

          <ExperimentEditor isFixed={false} />
        </S.BottomBar>
      )}
    </S.SidebarMenu>
  );
};

const mapStateToProps = (state: AppState) => ({
  tab: state.areas.sidebar.tab,
  previousTab: state.areas.sidebar.previousTab,
  me: state.areas.account.me,
  showFilter:
    state.areas.category.filter.showFilter || state.areas.sidebar.hasFilter,
  showHorizontalFilter: state.areas.category.filter.showHorizontalFilter,
});

const dispatchProps = {
  openLists: openListsAction,
  closeLists: closeListsAction,
  openAlerts: openAlertsAction,
  closeAlerts: closeAlertsAction,
  toggleFilter: filterActions.toggleFilter,
};

export default connect(mapStateToProps, dispatchProps)(SidebarMenuBar);
